import classnames from 'classnames'
import React from 'react'
import { Image } from '../../elements/Image'
import { Text } from '../../elements/Text'
import {
  Color,
  getColor,
  getColorTheme,
  HeadingStyle,
  HORIZONTAL_ALIGNMENT,
  ColorName as themeNames,
  mq,
} from '../../utils'
import { TextAlign } from '../../utils/constants/textAlign'
import { AllColors as BrandColors } from '../../utils/constants/themes'
import { LogoParty } from '../LogoParty'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'

export const GatedContentDefault = ({
  bgColor,
  heading,
  subheading,
  subheadingLinkText,
  subheadingLinkUrl,
  renderEmailForm,
  texturedBorder,
  image,
  enableBorders,
  logoParty,
  icon,
  eyebrow,
  headingType,
  compact,
  ctaHorizontalAlignment,
  fullWidth = true,
  type,
  headingFontStyle,
}: IProps): React.ReactElement => {
  const compatibleColor = getColor('default', bgColor || Color.White)
  const classes = classnames(
    'gated-content',
    `gated-content-theme theme-${themeNames[compatibleColor as BrandColors]}`,

    {
      'gated-content--image': image,
      'gated-content--logo-party': logoParty,
      'gated-content--textured-border': texturedBorder,
    },
  )

  const spacing = useSpacing()

  const background = classnames('background', `background--type-${type}`, {
    'background--ct': headingType === 'Graphik',
    compact: compact,
    'background--padding': !spacing,
  })

  const theme = getColorTheme('default', bgColor || Color.White)

  const showEyebrow = eyebrow || icon

  const getHeadingTextSize = (style?: HeadingStyle) => {
    switch (style) {
      case HeadingStyle.Large:
        return 'heading-2'
      case HeadingStyle.Small:
      default:
        return 'heading-3'
    }
  }

  return (
    <div className={classes}>
      <div className={background}>
        {/* Image? */}
        <div className="image">{image && <Image {...image} />}</div>
        {/* Left Column */}
        <div className="text-container">
          {/* Eyebrow */}
          {showEyebrow && (
            <div className="eyebrow-container">
              {icon && <img alt="icon" src={icon} className="icon" />}
              {eyebrow && (
                <Text size="eyebrows" brand="default">
                  {eyebrow}
                </Text>
              )}
            </div>
          )}

          {/* Heading */}
          {!logoParty && (
            <p className="heading">
              <Text brand="default" size={getHeadingTextSize(headingFontStyle)}>
                {heading}
              </Text>
            </p>
          )}

          {/* Subeading */}
          {subheading && (
            <div className="subheading">
              <Text brand="default" size="body">
                {subheading}
              </Text>
            </div>
          )}

          {subheadingLinkText && subheadingLinkUrl && (
            <div className="subheading-link">
              <a href={subheadingLinkUrl}>
                <Text brand="default" size="body">
                  {subheadingLinkText}
                </Text>
              </a>
            </div>
          )}

          {logoParty && (
            <LogoParty
              {...logoParty}
              headings={{
                heading: heading,
                style: HeadingStyle.Small,
                magicSparklesEffectEnabled: false,
              }}
              bgColor={bgColor || Color.White}
              textAlign={TextAlign.Center}
              noPadding
              fullWidth
            />
          )}

          {/* Image causes CTA to be nested inside of text container */}
          {image && <div className="email-form">{renderEmailForm()}</div>}
        </div>
        {/* Right Column */}
        {!image && <div className="email-form">{renderEmailForm()}</div>}
      </div>
      <style jsx>{`
        .gated-content-theme {
          background-color: ${bgColor ? theme.backgroundColor : 'transparent'};
          color: ${theme.textColor};
          width: ${fullWidth ? '100%' : '85%'};
          border-radius: ${fullWidth ? 'unset' : '25px'};
          max-width: ${fullWidth ? '100%' : '1290px'};
          margin: 0 auto;
        }

        .background {
          display: grid;
          background-size: cover;
          grid-template-columns:
            [gutter-left] 1fr
            [content] 12fr
            [gutter-right] 1fr;
          padding-left: 0;
          padding-right: 0;
          max-width: $container-max-width;
          margin: 0 auto;
          position: relative;

          &--padding {
            padding-top: 3.75rem;
            padding-bottom: 3.75rem;
          }

          &.compact {
            grid-template-columns:
              [gutter-left] 2fr
              [content] 12fr
              [gutter-right] 2fr;
          }
        }

        :global(.gated-content .image img) {
          box-shadow: 0px 5px 10px rgba(75, 75, 75, 0.11), 0px 5px 20px rgba(89, 89, 89, 0.12);
          border-radius: ${enableBorders ? '25px' : '0'};
        }

        :global(.gated-content--logo-party .logo) {
          margin: 8px 16px;
          max-width: 130px;
          max-height: 40px;
        }

        :global(.gated-content--logo-party .headings span) {
          font-size: 22px;
        }

        .image {
          display: none;
        }

        .text-container,
        .email-form {
          grid-column: content;
          text-align: center;
        }

        .text-container {
          padding-bottom: 20px;
        }

        .subheading {
          margin: 16px 0;
        }

        .subheading-link a {
          color: inherit;
          text-decoration: none;
          border-bottom: 1px solid;
        }

        .subheading-link a:hover,
        .subheading-link a:focus {
          border-bottom: 1px solid;
          outline: none;
        }

        .eyebrow-container {
          margin-bottom: 1.125rem;
          display: flex;
          align-items: center;
          gap: 0.875rem;
          justify-content: center;
        }

        .icon {
          width: 28px;
          height: 28px;
        }

        @media (${mq.laptop}) {
          .background {
            grid-template-columns:
              [gutter-left] 3fr
              [content] 8fr
              [gutter-right] 3fr;
          }

          .email-form {
            display: flex;
            justify-content: ${ctaHorizontalAlignment
              ? HORIZONTAL_ALIGNMENT[ctaHorizontalAlignment]
              : HORIZONTAL_ALIGNMENT['center']};
          }
        }

        @media (${mq.desktop}) {
          .background {
            grid-template-columns:
              [gutter-left] ${compact ? '2fr' : '1fr'}
              [text-container] ${compact ? '6fr' : '5fr'}
              [gap] 1fr
              [email-form] ${compact ? '3fr' : '4fr'}
              [gutter-right] ${compact ? '2fr' : '1fr'};
            padding-left: 0;
            padding-right: 0;

            &--padding {
              padding-top: ${compact ? '4rem' : '6.25rem'};
              padding-bottom: ${compact ? '4rem' : '6.25rem'};
            }

            &--ct {
              grid-template-columns:
                [gutter-left] 2fr
                [text-container] 4fr
                [gap] 1fr
                [email-form] 3fr
                [gutter-right] 2fr;
            }

            &.background--type-cta-link {
              grid-template-columns:
                [gutter-left] 2fr
                [text-container] 6fr
                [gap] 1fr
                [email-form] 3fr
                [gutter-right] 2fr;
            }
          }

          .eyebrow-container {
            justify-content: flex-start;
          }
          .gated-content--image {
            .background {
              grid-template-columns:
                [gutter-left] 1fr
                [image] 3fr
                [gap] 1fr
                [text-container] 6fr
                [gutter-right] 1fr;
            }

            .image {
              display: block;
              grid-column: image;
              max-width: 348px;
            }

            .email-form {
              margin-top: 15px;
              text-align: left;
              justify-content: ${ctaHorizontalAlignment
                ? HORIZONTAL_ALIGNMENT[ctaHorizontalAlignment]
                : HORIZONTAL_ALIGNMENT['center']};
            }
          }

          .gated-content--logo-party {
            .background {
              grid-template-columns:
                [gutter-left] 1fr
                [text-container] 6fr
                [gap] 1fr
                [email-form] 3fr
                [gutter-right] 1fr;
            }

            .email-form {
              margin-top: 15px;
              justify-content: ${ctaHorizontalAlignment
                ? HORIZONTAL_ALIGNMENT[ctaHorizontalAlignment]
                : HORIZONTAL_ALIGNMENT['center']};
            }
          }

          .text-container {
            grid-column: text-container;
            text-align: left;
            align-self: center;
          }

          .text-container {
            padding-bottom: 0;
          }

          .email-form {
            grid-column: email-form;
            align-self: center;
            display: flex;
            justify-content: ${ctaHorizontalAlignment
              ? HORIZONTAL_ALIGNMENT[ctaHorizontalAlignment]
              : HORIZONTAL_ALIGNMENT['center']};
          }
        }

        @media (${mq.desktopLg}) {
          .gated-content--logo-party {
            .background {
              grid-template-columns:
                [gutter-left] 1.5fr
                [text-container] 5fr
                [gap] 1fr
                [email-form] 3fr
                [gutter-right] 1.5fr;
            }
          }
        }
      `}</style>
    </div>
  )
}
